// @ts-strict-ignore
// @ts-check
import {appApiFetchJson, appApiUrl, djangoFetch} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';
import {ComplianceJob, RequestType} from 'common/report/queries/public-api/compliance-client';
import {toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {
  GetDiscoverabilityResponse,
  GetDiscoverableDomainsResponse,
  GetDiscoverableOrgsResponse,
  SetDiscoverabilityRequest,
} from 'app_api/organizations/domains/types';

import {
  AddProjectsToTeamsResponse,
  AddProjectsToTeamTeamDescriptor,
  AddUsersToProjectsResponse,
  AddUsersToTeamsResponse,
  DeleteResponse,
  DeleteUserResponse,
  DetailedUser,
  DomainClaimResponse,
  DomainGetResponse,
  DomainTransferResponse,
  DomainVerifyResponse,
  DomainWithdrawClaimResponse,
  GdprApiResponse,
  IdMergeStatusResponse,
  OrganizationsPayload,
  Project,
  ProjectPermissionsPayload,
  ProjectRolesPayload,
  ProjectUserGrantDescriptor,
  RoleDescriptor,
  Sso,
  Team,
  TeamMembershipDescriptor,
  UndeleteResponse,
  UpdateDefaultClusterIdResponse,
  UpdateOrgDiscoverableDomains,
  UpdateOrgTeamRequest,
  UpdateOrgUserRequest,
  UpdateRequest,
  UpdateResponse,
  UserInvite,
  UserInviteResponse,
} from './org-types';

/**
 * app api endpoint for organizations
 */
export function orgsUrl(organizationId = 0): string {
  return `/organizations/${organizationId || ``}`;
}

/**
 * app api url for organization domains endpoints
 */
export function domainsUrl(organizationId: number, endpoint: string): string {
  return appApiUrl(`organizations/${organizationId}/domains/${endpoint}/`);
}

/**
 * app api endpoint for organization teams
 */
export function teamUrl(organizationId: number, teamId = 0): string {
  const teamString = teamId || ``;
  return `/organizations/${organizationId}/teams/${teamString}`;
}

/**
 * app api endpoint for organization users
 */
export function userUrl(organizationId: number, email = ``): string {
  return `/organizations/${organizationId}/users/${encodeURIComponent(email)}`;
}

/**
 * GET /organizations/<organizationId>/domains/get-discoverability/
 * @param {number} organizationId: ID of org to get discoverability for
 */
export async function fetchOrgDiscoverability(organizationId: number): Promise<GetDiscoverabilityResponse[`results`]> {
  return appApiFetchJson(domainsUrl(organizationId, `get-discoverability`), {method: `get`});
}

/**
 * POST /organizations/<organizationId>/domains/set-discoverability/
 * @param {number} organizationId: ID of org to set discoverability for
 * @param {SetDiscoverabilityRequest} discoverability: discoverable value for org
 */
export async function updateOrgDiscoverability(
  organizationId: number,
  discoverability: SetDiscoverabilityRequest,
): Promise<UpdateResponse> {
  return appApiFetchJson(domainsUrl(organizationId, `set-discoverability`), {
    method: `post`,
    body: JSON.stringify(discoverability),
  });
}

/**
 * GET /organizations/<organizationId>/domains/get-discoverable-domains/
 */
export async function fetchOrgDiscoverableDomains(organizationId: number): Promise<GetDiscoverableDomainsResponse> {
  return appApiFetchJson(domainsUrl(organizationId, `get-discoverable-domains`), {method: `get`});
}

/**
 * POST /organizations/<organizationId>/domains/set-discoverable-domains/
 * @param {number} organizationId: ID of org to set domains for
 * @param {SetDiscoverableDomainsRequest} domains: discoverable domains for org
 */
export async function updateOrgDiscoverableDomains(
  organizationId: number,
  domains: SetDiscoverabilityRequest,
): Promise<UpdateOrgDiscoverableDomains> {
  return appApiFetchJson(domainsUrl(organizationId, `set-discoverable-domains`), {
    method: `post`,
    body: JSON.stringify(domains),
  });
}

/**
 * GET /organizations/get-discoverable-orgs/
 */
export async function fetchDiscoverableOrgs(): Promise<GetDiscoverableOrgsResponse[`results`]> {
  return appApiFetchJson(appApiUrl(`organizations/get-discoverable-orgs`), {method: `get`});
}

/**
 * POST /organizations/<organizationId>/update
 */
export async function updateOrganization(organizationId: number, attrs: UpdateRequest): Promise<UpdateResponse> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/update/`, {
    method: `post`,
    body: JSON.stringify(attrs),
  });
}

/**
 * POST /organizations/<organizationId>/scim-name
 */
export async function updateOrganizationSCIMName(organizationId: number, scimName: string): Promise<UpdateResponse> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/scim-name/`, {
    method: `post`,
    body: JSON.stringify({scim_name: scimName}),
  });
}

/**
 * GET /organizations/<organizationId>/scim-name
 */
export async function getOrganizationSCIMName(organizationId: number): Promise<UpdateResponse> {
  const url = `${orgsUrl(organizationId)}/scim-name/`;
  return appApiFetchJson(url, {
    method: `get`,
  });
}

/**
 * GET /organizations/<organizationId>/spark-settings
 */
export async function fetchSparkSettings(organizationId: number): Promise<UpdateResponse> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/spark-settings`, {
    method: `get`,
  });
}

/**
 * POST /organizations/<organizationId>/update-spark-settings
 */
export async function updateSparkSettings(organizationId: number, attrs: UpdateRequest): Promise<UpdateResponse> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/update-spark-settings`, {
    method: `post`,
    body: JSON.stringify(attrs),
  });
}

/**
 * Fetch data about the organization's projects
 * @param {number} organizationId - org ID
 * @param {object} kwargs
 * @param {boolean} [kwargs.includeSessionSettings] - boolean whether or not to include session settings in the payload
 * @param {boolean} [kwargs.includeTimePeriodSettings] - boolean whether or not to include time period settings in the payload
 * @param {boolean} [kwargs.includeExperimentSettings] - boolean whether or not to include experiment settings in the payload
 * @param {boolean} [kwargs.includeProjectFeatureflags] - boolean whether or not to include featureflags in the payload
 * @param {boolean} [kwargs.includeProjectUsageData] - boolean whether or not to include project usage data in the payload
 * @returns {Promise<Project[]>} array of projects
 */
export async function fetchOrgProjectData(
  organizationId: number,
  {
    includeSessionSettings = false,
    includeTimePeriodSettings = false,
    includeProjectFeatureflags = false,
    includeProjectUsageData = false,
    includeProjectShareSettings = false,
    includeExperimentSettings = false,
  }: {
    includeSessionSettings?: boolean;
    includeTimePeriodSettings?: boolean;
    includeProjectFeatureflags?: boolean;
    includeProjectUsageData?: boolean;
    includeProjectShareSettings?: boolean;
    includeExperimentSettings?: boolean;
  } = {},
): Promise<Project[]> {
  return appApiFetchJson(
    `${orgsUrl(organizationId)}/projects/`,
    {method: `get`},
    {
      include_session_settings: includeSessionSettings,
      include_time_period_settings: includeTimePeriodSettings,
      include_project_featureflags: includeProjectFeatureflags,
      include_project_usage_data: includeProjectUsageData,
      include_project_share_settings: includeProjectShareSettings,
      include_experiment_settings: includeExperimentSettings,
    },
  );
}

/**
 * Fetch Compliance Retrieval Jobs for an entire Organization
 */
export async function fetchOrgComplianceRetrievalJobs(
  organizationId: number,
  lastTs: number,
  sortOrder: number,
  filters?: Partial<ComplianceJob>,
): Promise<ComplianceJob[]> {
  const results = await appApiFetchJson(
    `${orgsUrl(organizationId)}/data-retrievals/`,
    {method: `get`},
    {from_ts: lastTs, order: sortOrder, ...(filters ?? {})},
  );
  return results.map((cj) => ({
    id: cj.id,
    requestTaskId: cj.tracking_id,
    status: cj.status,
    destinationUrl: cj.destination_url,
    projectId: cj.project_id,
    distinctIdCount: cj.distinct_id_count,
    dateRequested: cj.date_requested,
    requestingUser: cj.requesting_user,
    compliance: cj.compliance_type,
    requestType: RequestType.DATA_EXPORT,
    disclosureType: cj.disclosure_type,
  }));
}

/**
 * Fetch User Deletion Compliance Jobs for an entire Organization
 */
export async function fetchOrgComplianceDeletionJobs(
  organizationId: number,
  lastTs: number,
  sortOrder: number,
  filters?: Partial<ComplianceJob>,
): Promise<ComplianceJob[]> {
  const results = await appApiFetchJson(
    `${orgsUrl(organizationId)}/data-deletions/`,
    {method: `get`},
    {from_ts: lastTs, order: sortOrder, ...(filters ?? {})},
  );
  return results.map((cj) => ({
    id: cj.id,
    requestTaskId: cj.tracking_id,
    status: cj.status,
    requestingUser: cj.requesting_user,
    projectId: cj.project_id,
    distinctIdCount: cj.distinct_id_count,
    dateRequested: cj.date_requested,
    compliance: cj.compliance_type,
    requestType: RequestType.USER_DELETION,
  }));
}

/**
 * Fetch Compliance Jobs for an entire Organization
 */
export async function fetchOrgComplianceJobs(
  organizationId: number,
  complianceJobsLastRetrievalTs: number,
  complianceJobsLastDeletionTs: number,
  sortOrder: number,
  filters?: Partial<ComplianceJob>,
): Promise<{retrievals: ComplianceJob[]; deletions: ComplianceJob[]}> {
  const [retrievals, deletions] = await Promise.all([
    fetchOrgComplianceRetrievalJobs(organizationId, complianceJobsLastRetrievalTs, sortOrder, filters),
    fetchOrgComplianceDeletionJobs(organizationId, complianceJobsLastDeletionTs, sortOrder, filters),
  ]);

  return {retrievals, deletions};
}

export async function fetchGdprApiToken(): Promise<GdprApiResponse> {
  return appApiFetchJson(`/oauth/gdpr_api_token/`, {method: `GET`});
}

/**
 * Delete a subset of the organization's projects
 */
export async function deleteOrgProjects(organizationId: number, projectIds: number[]): Promise<DeleteResponse> {
  const url = `${orgsUrl(organizationId)}/delete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Undelete a subset of the organization's projects
 */
export async function undeleteOrgProjects(organizationId: number, projectIds: number[]): Promise<UndeleteResponse> {
  const url = `${orgsUrl(organizationId)}/undelete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Fetch data about the organization's teams
 */
export async function fetchOrgTeamData(organizationId: number): Promise<Team[]> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/teams/`, {
    method: `get`,
  });
}

export async function fetchOrgWorkspaceData(organizationId: number): Promise<WorkspaceObj[]> {
  const resp = await appApiFetchJson(`${orgsUrl(organizationId)}/workspaces/`, {
    method: `get`,
  });
  return resp.map(toWorkspaceObj);
}

/**
 * Delete organization teams
 */
export async function deleteOrgTeams(organizationId: number, teamIds: number[]): Promise<DeleteResponse> {
  const url = `${orgsUrl(organizationId)}/delete-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teamIds}),
  });
}

/**
 * Create organization teams
 * @param {string[]} teamNames - array of Team Names to create
 * @returns {Promise<string[]>} array of created team IDs
 */
export async function addOrgTeams(organizationId: number, teamNames: string[]): Promise<string[]> {
  const url = `${orgsUrl(organizationId)}/add-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teamNames}),
  });
}

/**
 * Update organizations default cluster id and project cluster id
 * @param {number} organizationId - ID of the organization to operate on
 * @param {number} projectId - ID of the project to update
 * @param {number} clusterId - the cluster id to set the default to
 * @returns {Promise<UpdateDefaultClusterIdResponse>} boolean result indicating success or failure
 */
export async function updateOrgDefaultAndProjectClusterId(
  organizationId: number,
  projectId: number,
  clusterId: number,
): Promise<UpdateDefaultClusterIdResponse> {
  const url = `${orgsUrl(organizationId)}/update-org-project-cluster/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({cluster_id: clusterId, project_id: projectId}),
  });
}

export async function addUsersToOrgProjects(
  organizationId: number,
  projectDescriptors: ProjectUserGrantDescriptor[],
): Promise<AddUsersToProjectsResponse> {
  const url = `${orgsUrl(organizationId)}/add-users-to-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projects: projectDescriptors}),
  });
}

export async function addUsersToOrgProject(
  organizationId: number,
  projectDescriptor: ProjectUserGrantDescriptor,
): Promise<AddUsersToProjectsResponse> {
  const url = `${orgsUrl(organizationId)}/add-users-to-project/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({
      ...projectDescriptor,
    }),
  });
}

/**
 * Add users to Teams
 */
export async function addUsersToOrgTeams(
  organizationId: number,
  teamDescriptors: TeamMembershipDescriptor[],
): Promise<AddUsersToTeamsResponse> {
  const url = `${orgsUrl(organizationId)}/add-users-to-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teams: teamDescriptors}),
  });
}

/**
 *
 * @param {number} organizationId - org ID
 * @param {AddProjectsToTeamTeamDescriptor[]} teamDescriptors - teams w/ embedded project descriptors
 * @returns {Promise<AddProjectsToTeamsResponse>}
 */
export async function addProjectsToOrgTeams(
  organizationId: number,
  teamDescriptors: AddProjectsToTeamTeamDescriptor[],
  addDefaultProjects = true,
): Promise<AddProjectsToTeamsResponse> {
  const url = `${orgsUrl(organizationId)}/add-projects-to-teams/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({teams: teamDescriptors, add_default_projects: addDefaultProjects}),
  });
}

/**
 * Remove users from Team
 */
export async function removeUsersFromOrgTeam(
  organizationId: number,
  teamId: number,
  userEmails: string[],
): Promise<DeleteUserResponse> {
  const url = `${teamUrl(organizationId, teamId)}/delete-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({userEmails}),
  });
}

/**
 * Remove projects from Team
 */
export async function removeProjectsFromOrgTeam(
  organizationId: number,
  teamId: number,
  projectIds: number[],
): Promise<DeleteResponse> {
  const url = `${teamUrl(organizationId, teamId)}/delete-projects/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({projectIds}),
  });
}

/**
 * Resend existing invites and extend their expiration date
 * @param {number} orgId - organization ID
 * @param {string[]} emails - array of email addresses to resend invites to
 * @returns {Promise<>} object with email keys and result of re-send
 */
export async function resendOrgInvites(orgId: number, emails: string[]): Promise<TODO> {
  const url = `${orgsUrl(orgId)}/resend-invites/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(emails),
  });
}

/**
 * Fetch data about the organization's users
 */
export async function fetchOrgUserData(organizationId: number): Promise<DetailedUser[]> {
  return appApiFetchJson(`${orgsUrl(organizationId)}/users/`, {
    method: `get`,
  });
}

/**
 * POST: Delete the organization's users
 */
export async function deleteOrgUsers(organizationId: number, userEmails: string[]): Promise<DeleteResponse> {
  const url = `${orgsUrl(organizationId)}/delete-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({userEmails}),
  });
}

/**
 * POST: Add the organization's users
 */
export async function addOrgUsers(
  organizationId: number,
  users: UserInvite[],
  onboardingParams?: {events: string[]; editor: string},
): Promise<UserInviteResponse[]> {
  const url = `${orgsUrl(organizationId)}/add-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(onboardingParams ? {users, onboarding_params: onboardingParams} : {users}),
  });
}

/**
 * POST: Add the organization's users using csv file
 * @param {number} organizationId - org ID
 * @param {File | FormData} attachment - csv file include users to add
 */
export async function addOrgUsersWithAttachment(
  organizationId: number,
  attachment: File | FormData,
): Promise<UserInviteResponse[]> {
  const url = `${orgsUrl(organizationId)}/bulk-add-users/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: attachment,
  });
}

/**
 * POST: Update an organization's team
 */
export async function updateOrgTeam(
  organizationId: number,
  teamId: number,
  request: UpdateOrgTeamRequest,
): Promise<UpdateResponse> {
  const url = `${teamUrl(organizationId, teamId)}/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(request),
  });
}

/**
 * POST: Update an organization's user
 * @param {number} organizationId - org ID
 * @param {string} email - email
 * @param {UpdateOrgUserRequest} request - params to update
 * @returns {Promise<UpdateResponse>} promise with response
 */
export async function updateOrgUser(
  organizationId: number,
  email: string,
  request: UpdateOrgUserRequest,
): Promise<UpdateResponse> {
  const url = `${userUrl(organizationId, email)}/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(request),
  });
}

export async function fetchOrgSsoData(organizationId: number): Promise<Sso> {
  const url = `${orgsUrl(organizationId)}/sso/`;
  return appApiFetchJson(url, {
    method: `get`,
  });
}

/**
 * @returns {Promise<boolean>} active flag of 2FA
 */
export async function fetchOrg2faData(organizationId: number): Promise<boolean> {
  const url = `${orgsUrl(organizationId)}/twofactor/`;
  return appApiFetchJson(url, {
    method: `get`,
  });
}

/**
 *
 * @param {number} organizationId - organization ID
 * @param {boolean} active - value to set twofactor_auth_required to
 * @param {string} secret - secret to confirm password
 * @returns {Promise<boolean>} new value
 */
export async function updateOrg2faData(organizationId: number, active: boolean, secret: string): Promise<boolean> {
  const url = `${orgsUrl(organizationId)}/twofactor/update/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({active, secret}),
  });
}

export async function getOrgProjectPermissions(organizationId: number): Promise<ProjectPermissionsPayload> {
  const url = `${orgsUrl(organizationId)}/project-permissions`;
  return appApiFetchJson(url, {method: `get`});
}

export async function getOrgAssignableProjectRoles(organizationId: number): Promise<ProjectRolesPayload> {
  const url = `${orgsUrl(organizationId)}/assignable-project-roles`;
  return appApiFetchJson(url, {method: `get`});
}

export async function getOrgAssignableOrgRoles(
  organizationId: number,
  forServiceAccounts = false,
): Promise<RoleDescriptor[]> {
  const url = `${orgsUrl(organizationId)}/assignable-org-roles`;
  return appApiFetchJson(url, {method: `get`}, {force_all: forServiceAccounts});
}

export async function getOrgEditableOrgRoles(organizationId: number): Promise<RoleDescriptor[]> {
  const url = `${orgsUrl(organizationId)}/assignable-org-roles`;
  return appApiFetchJson(url, {method: `get`}, {force_all: true});
}

export async function getOrgs(): Promise<OrganizationsPayload> {
  const url = `${orgsUrl()}`;
  return appApiFetchJson(url, {method: `get`});
}

/**
 *
 * @param targetOrgId {number} org ID to transfer to
 * @param projectId {number} project ID of project being transferred
 * @returns {Promise<TODO>}
 */
export async function transferProject(targetOrgId: number, projectId: number): Promise<TODO> {
  const url = `${orgsUrl(targetOrgId)}/transfer-project/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({project_id: projectId}),
  });
}

/**
 * Deletion using the project endpoint
 * @param projectId {number} project ID to delete
 * @returns {Promise<TODO>}
 */
export async function deleteProject(projectId: number): Promise<TODO> {
  const url = `/projects/delete/${projectId}/`;
  return djangoFetch(url);
}

/**
 * Undelete using the project endpoint
 * @param projectId {number} project ID to undelete
 * @returns {Promise<TODO}
 */
export async function undeleteProject(projectId: number): Promise<TODO> {
  const url = `/projects/undelete/${projectId}/`;
  return djangoFetch(url);
}

/**
 *
 * @param orgId {number} org ID to leave
 * @returns {Promise<DeleteUserResponse>} list of (one) email removed
 */
export async function leaveOrg(orgId: number): Promise<DeleteUserResponse> {
  const url = `${orgsUrl(orgId)}/leave/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify({}),
  });
}

export async function deleteUserAccount(): Promise<TODO> {
  const url = `/account/delete/`;
  return appApiFetchJson(url, {method: `post`});
}

/**
 * @param {number} orgId associated with invitation
 * @param {string[]} emails - list of emails to regenerate invites for
 * @returns {Promise<TODO>} obj with email keys and relative invite url values
 */
export async function regenerateOrgInviteUrls(orgId: number, emails: string[]): Promise<TODO> {
  const url = `${orgsUrl(orgId)}/regenerate-invite-url/`;
  return appApiFetchJson(url, {
    method: `post`,
    body: JSON.stringify(emails),
  });
}

/**
 * app api endpoint for domain claiming
 */
export function domainClaimUrl(): string {
  return `/security/domains/`;
}

export function fetchDomainData(orgId: number): Promise<DomainGetResponse[]> {
  return appApiFetchJson(`${domainClaimUrl()}${orgId}/`, {method: `GET`, body: null});
}

/**
 *
 * @param {number} orgId - ID of org to verify domain for
 * @param {string} domain - domain to verify claim for
 * @returns {Promise<DomainVerifyResponse>}
 */
export function verifyDomain(orgId: number, domain: string): Promise<DomainVerifyResponse> {
  return appApiFetchJson(`${domainClaimUrl()}verify/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to claim a domain for
 * @param {string} domain - domain for which to remove claim
 * @param {string} secret - secret to confirm password
 * @returns {Promise<DomainClaimResponse>}
 */
export function claimDomain(orgId: number, domain: string, secret: string): Promise<DomainClaimResponse> {
  return appApiFetchJson(`${domainClaimUrl()}claim/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain, secret}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to withdraw a claim for
 * @param {string} domain - domain for which to remove claim
 * @param {string} secret - secret to confirm password
 * @returns {Promise<DomainWithdrawClaimResponse>}
 */
export function withdrawDomainClaim(
  orgId: number,
  domain: string,
  secret: string,
): Promise<DomainWithdrawClaimResponse> {
  return appApiFetchJson(`${domainClaimUrl()}withdraw-claim/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain, secret}),
  });
}

/**
 *
 * @param {number} orgId - ID of org to withdraw a claim for
 * @param {string} domain - domain for which to remove claim
 * @param {string} secret - secret to confirm password
 * @returns {Promise<DomainTransferResponse>}
 */
export function transferDomainClaimToAdminOrg(
  orgId: number,
  domain: string,
  secret: string,
): Promise<DomainTransferResponse> {
  return appApiFetchJson(`${domainClaimUrl()}transfer-claim-to-admin-org/${orgId}/`, {
    method: `post`,
    body: JSON.stringify({domain, secret}),
  });
}

export function resetTwoFactorMethods(orgId, userEmails, secret) {
  return apiClient.post(`/security/twofactor/reset_twofactor/${orgId}/`, {user_emails: userEmails, secret});
}

export function enableIdMerge(orgId: number): Promise<TODO> {
  return appApiFetchJson(`${orgsUrl(orgId)}/enable-id-merge/`, {
    method: `post`,
  });
}

export function setDefaultIdMergeMode(orgId: number, mode: string): Promise<TODO> {
  return appApiFetchJson(`${orgsUrl(orgId)}/update/`, {
    method: `post`,
    body: JSON.stringify({default_id_merge_mode: mode}),
  });
}

export function getIdMergeStatus(orgId: number): Promise<IdMergeStatusResponse> {
  return appApiFetchJson(`${orgsUrl(orgId)}/get-id-merge-status/`, {
    method: `get`,
  });
}
